import React, { useEffect, useRef, useState } from 'react';
import { X, Loader2, CheckCircle2 } from 'lucide-react';

// ── Constants ─────────────────────────────────────────────────────────────────
const NATIVE_FORMATS = ['ean_13', 'ean_8', 'upc_a', 'upc_e', 'code_128', 'code_39'];

// Camera constraints — 640×480 decodes 1-D barcodes just as reliably as 1280×720
// but has half the pixel count, so each frame processes faster.
const CAM_CONSTRAINTS = {
  video: { facingMode: 'environment', width: { ideal: 640 }, height: { ideal: 480 } },
};

// How long (ms) to block re-detection after a successful scan in continuous mode.
const RESCAN_DELAY = 1000;

const supportsNativeDetector = typeof window !== 'undefined' && 'BarcodeDetector' in window;

// Eagerly pre-fetch ZXing so the dynamic import is already resolved by the time
// the user taps "Scan with camera". Only loaded when BarcodeDetector is absent.
const _zxingPromise = (!supportsNativeDetector && typeof window !== 'undefined')
  ? import('@zxing/browser').catch(() => null)
  : null;

// ── ZXing hint builder — only 1-D formats, TRY_HARDER removed ─────────────────
function buildZxingHints() {
  try {
    // require() is safe in CRA/webpack; keeps this out of the top-level import
    // so the heavy ZXing bundle is still covered by the existing lazy load.
    const { BarcodeFormat, DecodeHintType } = require('@zxing/library');
    return new Map([[DecodeHintType.POSSIBLE_FORMATS, [
      BarcodeFormat.EAN_13, BarcodeFormat.EAN_8,
      BarcodeFormat.UPC_A,  BarcodeFormat.UPC_E,
      BarcodeFormat.CODE_128, BarcodeFormat.CODE_39,
    ]]]);
  } catch {
    return new Map();
  }
}

// ── Beep: singleton AudioContext so we never hit the browser's instance cap ────
// A fresh context is created on first use; if the browser suspends it (autoplay
// policy) we resume it before playing. Never create more than one context.
let _audioCtx = null;
function playBeep() {
  try {
    if (!_audioCtx || _audioCtx.state === 'closed') {
      _audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    }
    const ctx = _audioCtx;
    const play = () => {
      const osc  = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = 'square';
      osc.frequency.value = 1800;
      gain.gain.setValueAtTime(0.15, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.12);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.12);
    };
    if (ctx.state === 'suspended') ctx.resume().then(play).catch(() => {});
    else play();
  } catch (_) {}
}

// ── Component ─────────────────────────────────────────────────────────────────
/**
 * Props
 *   onDetected(barcode)  — called with decoded string on each successful scan
 *   onClose()            — called when user taps ✕
 *   preWarmStream        — MediaStream pre-obtained by parent; skips getUserMedia delay
 *   continuous           — stay open after each scan; only ✕ closes the camera
 *   lastDetectedName     — product name shown briefly in the success overlay
 *   inline               — render as a compact page-embedded strip instead of full-screen overlay
 */
export default function CameraScanner({ onDetected, onClose, preWarmStream, continuous, lastDetectedName, inline }) {
  const videoRef      = useRef(null);
  const rafRef        = useRef(null);    // requestAnimationFrame handle
  const readerRef     = useRef(null);    // ZXing BrowserMultiFormatReader (fallback)
  const streamRef     = useRef(null);    // stream we own (never preWarmStream)
  const pausedRef     = useRef(false);   // true during RESCAN_DELAY cooldown
  const onDetectedRef = useRef(onDetected);
  const onCloseRef    = useRef(onClose);
  const continuousRef = useRef(continuous);

  const retryRef = useRef(null); // holds the retry-camera function

  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [flashOk, setFlashOk] = useState(false);

  // Keep refs in sync without re-running effects
  useEffect(() => { onDetectedRef.current  = onDetected;  }, [onDetected]);
  useEffect(() => { onCloseRef.current     = onClose;     }, [onClose]);
  useEffect(() => { continuousRef.current  = continuous;  }, [continuous]);

  // ── Stop all decoding and release owned tracks ─────────────────────────────
  // Defined early via ref so it's callable from inside async callbacks and the
  // RAF loop without creating circular useCallback dependencies.
  const stopAllRef = useRef(null);
  stopAllRef.current = () => {
    cancelAnimationFrame(rafRef.current);
    try { readerRef.current?.reset(); } catch (_) {}
    // Only stop tracks we own — never stop the parent's preWarmStream.
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
  };

  // ── Scan success ───────────────────────────────────────────────────────────
  // Stored in a ref so the RAF tick (which closes over this ref) always sees the
  // latest version without needing to be recreated.
  const handleDetectedRef = useRef(null);
  handleDetectedRef.current = (text) => {
    if (pausedRef.current) return;
    pausedRef.current = true;

    playBeep();
    navigator.vibrate?.(150);
    onDetectedRef.current(text);

    if (!continuousRef.current) {
      stopAllRef.current();
      return;
    }

    // Continuous mode: flash green border, re-arm after cooldown.
    // The RAF loop keeps running but skips detection while pausedRef is true.
    setFlashOk(true);
    setTimeout(() => {
      pausedRef.current = false;
      setFlashOk(false);
    }, RESCAN_DELAY);
  };

  // ── Native BarcodeDetector RAF loop ───────────────────────────────────────
  const startNativeLoop = (video) => {
    const detector = new window.BarcodeDetector({ formats: NATIVE_FORMATS });

    const tick = async () => {
      if (!videoRef.current) return; // component unmounted

      if (!pausedRef.current && video.readyState >= 2) {
        try {
          const results = await detector.detect(video);
          if (results.length) {
            handleDetectedRef.current(results[0].rawValue);
          }
        } catch {
          // decode error on this frame — continue
        }
      }

      // Always re-schedule so the loop keeps alive during the cooldown pause.
      rafRef.current = requestAnimationFrame(tick);
    };

    rafRef.current = requestAnimationFrame(tick);
  };

  // ── ZXing fallback loop ────────────────────────────────────────────────────
  const startZxingLoop = async (stream) => {
    const { BrowserMultiFormatReader } = await (_zxingPromise || import('@zxing/browser'));
    if (!videoRef.current) return; // unmounted during async import

    const hints  = buildZxingHints();
    const reader = new BrowserMultiFormatReader(hints);
    readerRef.current = reader;

    const vid = videoRef.current;
    vid.srcObject = stream;
    await vid.play().catch(() => {});
    setLoading(false);

    reader.decodeFromStream(stream, vid, (result) => {
      if (result) handleDetectedRef.current(result.getText());
    });
  };

  // ── Attach a stream and start decoding ────────────────────────────────────
  const startDecoding = async (stream) => {
    const vid = videoRef.current;
    if (!vid) return;

    // Install WASM polyfill on iOS Safari / Samsung Internet where native
    // BarcodeDetector is absent. ZXing's canvas drawImage path silently
    // produces blank frames on WebKit, so the polyfill is strongly preferred.
    if (!('BarcodeDetector' in window)) {
      try {
        const { BarcodeDetectorPolyfill } = await import('@undecaf/barcode-detector-polyfill');
        window.BarcodeDetector = BarcodeDetectorPolyfill;
      } catch {
        // polyfill unavailable — ZXing fallback below
      }
    }

    if ('BarcodeDetector' in window) {
      vid.srcObject = stream;
      vid.muted = true; // required for autoplay on Android WebView
      try {
        await Promise.race([
          vid.play(),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 10000)),
        ]);
        setLoading(false);
        startNativeLoop(vid);
      } catch (e) {
        setLoading(false);
        setError(e.message === 'timeout'
          ? 'Camera taking too long. Tap Retry or restart the app.'
          : 'Could not start camera. Make sure no other app is using it.');
      }
    } else {
      // True last resort: ZXing (may have canvas issues on some WebKit versions)
      await startZxingLoop(stream);
    }
  };

  // ── Mount: obtain stream (or use pre-warmed) and start decoding ────────────
  useEffect(() => {
    pausedRef.current = false;

    const go = async () => {
      stopAllRef.current(); // clean up any previous stream before retrying
      setLoading(true);
      setError(null);
      if (preWarmStream?.active) {
        await startDecoding(preWarmStream);
      } else {
        try {
          const stream = await navigator.mediaDevices.getUserMedia(CAM_CONSTRAINTS);
          streamRef.current = stream;
          await startDecoding(stream);
        } catch (e) {
          setLoading(false);
          setError(e?.message?.includes('ermission')
            ? 'Camera permission denied. Allow camera access and try again.'
            : 'Could not start camera. Make sure no other app is using it.');
        }
      }
    };

    retryRef.current = go;
    go();
    return () => stopAllRef.current();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-retry when user returns from Android Settings after granting permission
  useEffect(() => {
    if (!error?.includes('ermission')) return;
    const handler = () => {
      if (document.visibilityState === 'visible') retryRef.current?.();
    };
    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [error]);

  // ── Render ─────────────────────────────────────────────────────────────────
  const handleClose = () => { stopAllRef.current(); onCloseRef.current(); };

  if (inline) {
    // ── Inline strip — GenZ pastel aesthetic ─────────────────────────────────
    return (
      <div className="relative w-full overflow-hidden" style={{
        height: '130px',
        background: 'linear-gradient(160deg, #0e0a1f 0%, #080d1c 100%)',
      }}>

        <style>{`
          @keyframes kf-aurora  { 0% { left:-55% } 60%,100% { left:145% } }
          @keyframes kf-cbreathe { 0%,100% { filter:drop-shadow(0 0 3px rgba(255,255,255,0.7)) } 50% { filter:drop-shadow(0 0 9px rgba(255,255,255,0.95)) drop-shadow(0 0 18px rgba(255,255,255,0.3)) } }
          @keyframes kf-recdot  { 0%,100% { opacity:1; transform:scale(1) } 50% { opacity:0.3; transform:scale(0.7) } }
          @keyframes kf-sring   { 0% { transform:scale(1); opacity:0.8 } 100% { transform:scale(1.5); opacity:0 } }
          @keyframes kf-floatup { 0% { transform:translateY(4px); opacity:0 } 60% { opacity:1 } 100% { transform:translateY(-4px); opacity:0 } }
          .cam-aurora   { animation: kf-aurora   3.2s ease-in-out infinite; }
          .cam-cbreathe { animation: kf-cbreathe 2s ease-in-out infinite; }
          .cam-recdot   { animation: kf-recdot   1.3s ease-in-out infinite; }
          .cam-sring    { animation: kf-sring    0.55s ease-out forwards; }
          .cam-floatup  { animation: kf-floatup  2.6s ease-in-out infinite; }
        `}</style>

        {/* Live camera feed */}
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />

        {/* Loading — fully opaque so the raw camera hardware preview doesn't show through */}
        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center" style={{ background: '#0e0a1f' }}>
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-6 w-6 animate-spin" style={{ color: '#c4b5fd' }} />
              <span className="text-xs" style={{ color: 'rgba(196,181,253,0.65)' }}>Starting camera…</span>
            </div>
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="absolute inset-0 flex items-center justify-center px-4" style={{ background: 'rgba(14,10,31,0.88)' }}>
            <div className="rounded-2xl p-3 text-center w-full max-w-xs" style={{ background: 'rgba(255,255,255,0.96)' }}>
              <p className="text-xs text-gray-600 mb-1">{error}</p>
              {error.includes('ermission') && (
                <p className="text-[10px] text-gray-400 mb-2">Go to Settings → allow camera, then tap Retry</p>
              )}
              <div className="flex gap-2 justify-center">
                <button onClick={() => retryRef.current?.()} className="px-3 py-1.5 rounded-xl text-xs font-semibold"
                  style={{ background: 'linear-gradient(135deg,#c4b5fd,#a78bfa)', color: '#fff' }}>Retry</button>
                <button onClick={handleClose} className="px-3 py-1.5 rounded-xl text-xs font-semibold border border-gray-200 text-gray-500">Close</button>
              </div>
            </div>
          </div>
        )}

        {/* Scan frame */}
        {!loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="relative" style={{ width: '70%', height: '52px' }}>

              {/* Aurora shimmer — clipped inside frame */}
              <div className="absolute inset-0 overflow-hidden" style={{ borderRadius: 8 }}>
                {!flashOk && (
                  <div className="cam-aurora absolute top-0 bottom-0" style={{
                    width: '50%',
                    background: 'linear-gradient(105deg, transparent 0%, rgba(244,114,182,0.22) 20%, rgba(196,181,253,0.32) 40%, rgba(134,239,172,0.2) 60%, rgba(147,197,253,0.24) 80%, transparent 100%)',
                    filter: 'blur(4px)',
                  }} />
                )}
                {/* Success soft fill */}
                {flashOk && (
                  <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(196,181,253,0.22), rgba(134,239,172,0.2))' }} />
                )}
              </div>

              {/* Soft pastel corner brackets */}
              {[
                { top: 0,    left: 0,   borderTop: '2.5px solid', borderLeft: '2.5px solid',  borderRadius: '6px 0 0 0' },
                { top: 0,    right: 0,  borderTop: '2.5px solid', borderRight: '2.5px solid', borderRadius: '0 6px 0 0' },
                { bottom: 0, left: 0,   borderBottom: '2.5px solid', borderLeft: '2.5px solid',  borderRadius: '0 0 0 6px' },
                { bottom: 0, right: 0,  borderBottom: '2.5px solid', borderRight: '2.5px solid', borderRadius: '0 0 6px 0' },
              ].map((s, i) => (
                <div key={i}
                  className={flashOk ? 'absolute' : 'absolute cam-cbreathe'}
                  style={{ ...s, width: 22, height: 22, borderColor: flashOk ? '#34d399' : '#ffffff', transition: 'border-color 0.2s' }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Success ripple */}
        {flashOk && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className="cam-sring rounded-full" style={{ width: 70, height: 70, border: '2px solid #34d399' }} />
          </div>
        )}

        {/* Success label — soft mint-to-lavender gradient pill */}
        {flashOk && lastDetectedName && (
          <div className="absolute bottom-2.5 left-0 right-0 flex justify-center pointer-events-none">
            <div className="flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
              style={{ background: 'linear-gradient(135deg,#86efac,#6ee7b7)', color: '#064e3b', boxShadow: '0 4px 14px rgba(134,239,172,0.4)' }}>
              <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
              <span className="max-w-[200px] truncate">{lastDetectedName}</span>
            </div>
          </div>
        )}

        {/* Floating close button — top-right corner, no bar */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 z-10 flex items-center justify-center rounded-full"
          style={{ width: 26, height: 26, background: 'rgba(196,181,253,0.18)', border: '1px solid rgba(196,181,253,0.25)' }}
          aria-label="Close camera"
        >
          <X className="h-3.5 w-3.5" style={{ color: 'rgba(220,210,255,0.9)' }} />
        </button>
      </div>
    );
  }

  // ── Full-screen overlay mode (default) ──────────────────────────────────────
  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">

      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 text-white shrink-0">
        <span className="font-semibold text-base">
          {continuous ? 'Scan items — tap ✕ when done' : 'Scan barcode'}
        </span>
        <button onClick={handleClose} className="p-1 rounded-full hover:bg-white/10 transition-colors" aria-label="Close camera">
          <X className="h-6 w-6" />
        </button>
      </div>

      {/* Video */}
      <div className="flex-1 relative overflow-hidden">
        <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" playsInline muted />

        {loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="flex flex-col items-center gap-3 text-white">
              <Loader2 className="h-8 w-8 animate-spin" />
              <span className="text-sm">Starting camera…</span>
            </div>
          </div>
        )}

        {error && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80 px-6">
            <div className="bg-white rounded-xl p-5 text-center max-w-xs">
              <p className="text-sm text-gray-700 mb-4">{error}</p>
              <button onClick={handleClose} className="px-4 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium">
                Close
              </button>
            </div>
          </div>
        )}

        {/* Scan frame — flashes green on success */}
        {!loading && !error && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <div className={`w-64 h-40 rounded-lg shadow-[0_0_0_9999px_rgba(0,0,0,0.45)] transition-colors duration-150 ${
              flashOk ? 'border-4 border-emerald-400' : 'border-2 border-white/70'
            }`} />
          </div>
        )}

        {/* Success label (continuous mode) */}
        {flashOk && lastDetectedName && (
          <div className="absolute bottom-16 left-0 right-0 flex justify-center pointer-events-none">
            <div className="flex items-center gap-2 bg-emerald-600/90 text-white rounded-full px-4 py-2 text-sm font-medium shadow-lg">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span className="max-w-[220px] truncate">{lastDetectedName}</span>
            </div>
          </div>
        )}
      </div>

      {!error && (
        <p className="text-white/60 text-center text-xs py-4 shrink-0">
          {flashOk ? 'Added ✓ — keep scanning…' : 'Point camera at barcode — it will scan automatically'}
        </p>
      )}
    </div>
  );
}
